import type { IProcessOptionalString } from "../process-things";
import type { ProcessTaskModelTypeName } from "../process-task-model-type-names";
import type { InsertionRewriteBlockedReason } from "../process-task-relink";

/** What the copied tasks become in the target: tasks, whole phases or whole areas, depending on the "+" clicked. */
export enum ProcessModelCopyLevel {
  Task = "Task",
  Phase = "Phase",
  Area = "Area",
}

/** The kinds of elements of a process model below the model itself. */
export enum ProcessModelCopyNodeType {
  Area = "Area",
  Phase = "Phase",
  Task = "Task",
}

/** The model itself, which an id conflict can be with as well: its modelId has to be unique among its elements too. */
export enum ProcessModelCopyModelNodeType {
  Model = "Model",
}

/** What a copied element's modelId can be taken by in the target. */
export type ProcessModelCopyExistingNodeType = ProcessModelCopyNodeType | ProcessModelCopyModelNodeType;

/**
 * Where copied elements come from: a model of the own organization, the latest shared release of an Ankaadia template,
 * or a model of another organization as it is now: one where the user's account administers processes, or any for a
 * super-user.
 */
export enum ProcessModelCopySourceKind {
  OrganizationModel = "OrganizationModel",
  SharedRelease = "SharedRelease",
  OtherOrganizationModel = "OtherOrganizationModel",
}

/** What happens to a copied element whose modelId already exists in the target model. */
export enum ProcessModelCopyIdAction {
  /** Copy it with another modelId. */
  Rename = "Rename",
  /** Don't copy it: references point to the existing element, and copied children go into it. */
  UseExisting = "UseExisting",
}

/** What happens to a reference of a copied task to an element that is not copied. */
export enum ProcessModelCopyReferenceAction {
  /** Point to the given element of the target model instead. */
  Link = "Link",
  /** Drop the reference. Only for lists and fields of element ids, not for expressions. */
  Remove = "Remove",
  /** Leave the reference as it is. */
  Keep = "Keep",
}

/** Kinds of role conflicts. */
export enum ProcessModelCopyRoleConflictKind {
  /** The target model does not define the role key. */
  MissingRole = "MissingRole",
  /** The target model defines the key, but with another name, `isOptional` or `visibleStates`. */
  RoleDefinitionDiffers = "RoleDefinitionDiffers",
}

/** Kinds of reference conflicts. */
export enum ProcessModelCopyReferenceConflictKind {
  /** A field references a task that is neither copied nor in the target model. */
  MissingReference = "MissingReference",
  /** An expression, Blockly workspace or rule target references an element that is neither copied nor in the target model. */
  UnresolvedExpressionReference = "UnresolvedExpressionReference",
  /** The referenced element is not copied, but the target model has one with the same modelId. */
  LinkedToExistingElement = "LinkedToExistingElement",
}

/** Where a reference is written down. */
export enum ProcessModelCopyReferenceType {
  /** A field holding element ids, e.g. predecessors. */
  Field = "Field",
  /** The targets of a rule. */
  RuleTarget = "RuleTarget",
  /** An expression quoting the id. */
  Expression = "Expression",
  /** A Blockly workspace selecting the element. */
  Blockly = "Blockly",
}

/** Why a resolution was suggested. */
export enum ProcessModelCopyMatchReason {
  /** The target has an element (or role) with the same id or key. */
  SameId = "SameId",
  /** The target has exactly one element (or role) with the same name. */
  SameLabel = "SameLabel",
  /**
   * The target has several elements (or roles) with the same name. The user has to pick an element; of the roles the
   * first in the target's order is suggested, for the user to check.
   */
  AmbiguousLabel = "AmbiguousLabel",
  /** Nothing in the target matches. */
  NoMatch = "NoMatch",
}

/** Why a copy cannot be applied as planned. */
export enum ProcessModelCopyBlockingReason {
  /** A conflict has no suggestion and no valid decision. */
  UnresolvedConflict = "UnresolvedConflict",
  /** A copied task's type needs an add-on the target organization does not have. */
  TaskTypeNotAllowed = "TaskTypeNotAllowed",
  /** Every selected element exists in the target already and nothing would be copied. */
  NothingToCopy = "NothingToCopy",
}

/** Why no copy could be planned at all. */
export enum ProcessModelCopyPlanError {
  /** The container does not exist in the target, or holds other elements than the level copies. */
  InvalidTarget = "InvalidTarget",
  /** Nothing is selected, or a selected task is no task of the source (any more). */
  InvalidSelection = "InvalidSelection",
}

// ---------------------------------------------------------------------------------------------------------------------
// The models the copy reads. Structural, so the source's full content (JSON) and the target's tree both fit.
// ---------------------------------------------------------------------------------------------------------------------

/** A role definition of a process model. */
export interface ProcessModelCopyRole {
  processRole: string;
  displayName?: IProcessOptionalString[];
  isOptional?: boolean;
  visibleStates?: string[];
}

/** An area, phase or task with any further fields of its type, which a copy carries over as they are. */
export interface ProcessModelCopyElement extends Record<string, unknown> {
  modelId: string;
  _typeName?: string;
  displayName?: IProcessOptionalString[];
  visibleForRoles?: string[];
}

export interface ProcessModelCopyRule extends Record<string, unknown> {
  id?: string;
  propertyName?: string;
  expression?: string;
}

/** The exam settings of an exam task, which pair it with its partner at runtime. */
export interface ProcessModelCopyLanguageEducationFields {
  examType?: string;
  languageLevel?: string;
  examModules?: string[];
}

export interface ProcessModelCopyTask extends ProcessModelCopyElement {
  targetObject?: string;
  predecessorsIds?: string[];
  atLeastOnePredecessorIds?: string[];
  rules?: ProcessModelCopyRule[];
  languageEducationFields?: ProcessModelCopyLanguageEducationFields;
}

export interface ProcessModelCopyPhase extends ProcessModelCopyElement {
  tasks?: ProcessModelCopyTask[];
}

export interface ProcessModelCopyArea extends ProcessModelCopyElement {
  phases?: ProcessModelCopyPhase[];
}

/** A process model: the source to copy from (its full content), or the target (its tree is enough). */
export interface ProcessModelCopyModel {
  modelId: string;
  displayName?: IProcessOptionalString[];
  rootLanguage: string;
  languages?: string[];
  roles?: ProcessModelCopyRole[];
  areas?: ProcessModelCopyArea[];
}

// ---------------------------------------------------------------------------------------------------------------------
// The user's decisions
// ---------------------------------------------------------------------------------------------------------------------

/** A role key the copied elements use is mapped to a role of the target model, in every field and expression. */
export interface ProcessModelCopyRoleDecision {
  roleKey: string;
  targetRoleKey: string;
}

export interface ProcessModelCopyIdDecision {
  sourceModelId: string;
  action: ProcessModelCopyIdAction;
  /** A modelId of the user's choice, for `Rename`; only taken where the user may choose modelIds. */
  newModelId?: string;
}

export interface ProcessModelCopyReferenceDecision {
  /** The copied task holding the reference. */
  sourceModelId: string;
  /** Where the task holds it, e.g. `predecessorsIds` or `rules.0.expression`. */
  field: string;
  referencedModelId: string;
  action: ProcessModelCopyReferenceAction;
  /** The target element to point to instead, for `Link`. */
  targetModelId?: string;
}

/** The user's decisions on conflicts. A conflict without one takes its suggestion. */
export interface ProcessModelCopyDecisions {
  roleDecisions?: ProcessModelCopyRoleDecision[];
  idDecisions?: ProcessModelCopyIdDecision[];
  referenceDecisions?: ProcessModelCopyReferenceDecision[];
  /** Target tasks that keep waiting for the task before the insertion point only, without the copied ones. */
  skippedRewireTaskModelIds?: string[];
}

// ---------------------------------------------------------------------------------------------------------------------
// Conflicts
// ---------------------------------------------------------------------------------------------------------------------

export interface ProcessModelCopyRoleResolution {
  targetRoleKey: string;
  reason?: ProcessModelCopyMatchReason;
}

export interface ProcessModelCopyRoleCandidate {
  roleKey: string;
  displayName: string;
}

/** Where a copied element uses a role: a role field, or an expression literal. */
export interface ProcessModelCopyUsage {
  sourceModelId: string;
  field: string;
}

/** Conflicts #1, #2 and #8: a role key the copied elements use. */
export interface ProcessModelCopyRoleConflict {
  /** Stable across plans, so the wizard keeps the user's choice. */
  key: string;
  kind: ProcessModelCopyRoleConflictKind;
  roleKey: string;
  /** The source model's name of the role, empty if it does not define it. */
  displayName: string;
  usages: ProcessModelCopyUsage[];
  /** Expressions passing the role to `getOrganizationId` or rules writing it, which name the mapped role instead. */
  literalUsages: ProcessModelCopyUsage[];
  /** Target roles with the same name, when there are several to pick from. */
  candidates: ProcessModelCopyRoleCandidate[];
  /** None when no target role has the role's name: the user picks one, and the copy waits for it. */
  suggestion?: ProcessModelCopyRoleResolution;
  /** What the copy does: the user's decision, or else the suggestion. */
  resolution?: ProcessModelCopyRoleResolution;
  isReviewRequired: boolean;
  isResolved: boolean;
  /** The decision given could not be applied; the suggestion is used instead. */
  hasInvalidDecision: boolean;
}

export interface ProcessModelCopyIdResolution {
  action: ProcessModelCopyIdAction;
  newModelId?: string;
  reason?: ProcessModelCopyMatchReason;
}

/** Conflict #3: a copied element whose modelId the target has already. */
export interface ProcessModelCopyIdConflict {
  key: string;
  sourceModelId: string;
  nodeType: ProcessModelCopyNodeType;
  displayName: string;
  existingNodeType: ProcessModelCopyExistingNodeType;
  existingDisplayName: string;
  suggestion: ProcessModelCopyIdResolution;
  resolution: ProcessModelCopyIdResolution;
  /** Whether the user may type a modelId of their own (Ankaadia users only). */
  canChooseModelId: boolean;
  isReviewRequired: boolean;
  isResolved: boolean;
  hasInvalidDecision: boolean;
}

export interface ProcessModelCopyReferenceResolution {
  action: ProcessModelCopyReferenceAction;
  targetModelId?: string;
  reason?: ProcessModelCopyMatchReason;
}

export interface ProcessModelCopyReferenceCandidate {
  modelId: string;
  displayName: string;
}

/** Conflicts #4, #5 and #6: a reference of a copied task to an element that is not copied. */
export interface ProcessModelCopyReferenceConflict {
  key: string;
  kind: ProcessModelCopyReferenceConflictKind;
  referenceType: ProcessModelCopyReferenceType;
  /** The kind of element the reference points to. */
  referencedNodeType: ProcessModelCopyNodeType;
  sourceModelId: string;
  field: string;
  referencedModelId: string;
  /** The source model's name of the referenced element, empty when the source does not have it either. */
  referencedDisplayName: string;
  /** The expression or Blockly workspace, for references written into one. */
  expression?: string;
  /** Target elements with the same name, when there are several to pick from. */
  candidates: ProcessModelCopyReferenceCandidate[];
  suggestion?: ProcessModelCopyReferenceResolution;
  resolution?: ProcessModelCopyReferenceResolution;
  isReviewRequired: boolean;
  isResolved: boolean;
  hasInvalidDecision: boolean;
}

/** Conflict #7: a target task waiting only for the task before the insertion point. */
export interface ProcessModelCopyRewireCandidate {
  key: string;
  taskModelId: string;
  displayName: string;
  previousPredecessorsIds: string[];
  /** What it waits for then: the copied tasks, and the task before the insertion point unless they wait for it. */
  predecessorsIds: string[];
  blockedReason?: InsertionRewriteBlockedReason;
  /** False when the user unticked it or it is blocked. */
  isApplied: boolean;
}

/** Conflict #9: a copied element's text that the target's root language lacked, taken from the source's root language. */
export interface ProcessModelCopyTranslationFallback {
  sourceModelId: string;
  modelId: string;
  field: string;
  language: string;
  value: string;
}

/** Conflict #10: organization-scoped ids that cannot be valid in the target organization and were cleared. */
export interface ProcessModelCopyResourceFinding {
  sourceModelId: string;
  field: string;
  values: string[];
}

/**
 * The exam tasks, which pair up at runtime: an exam assignment activates the task entering the result of the same exam,
 * and that one assigns the candidate again after a failed exam.
 */
export type ProcessModelCopyExamTaskTypeName = Extract<
  ProcessTaskModelTypeName,
  "ProcessTaskEducationAssignToExamModel" | "ProcessTaskEducationEnterExamResultModel"
>;

/** A copied exam task without a partner, neither among the copied tasks nor in the target model. Only a hint. */
export interface ProcessModelCopyExamPairingFinding {
  sourceModelId: string;
  modelId: string;
  typeName: ProcessModelCopyExamTaskTypeName;
}

/**
 * The target area or phase the copy goes into is not visible for roles the copied tasks are visible for, target or are
 * handled by: whoever has only those roles does not see the container, and so not the tasks in it. Only a hint.
 */
export interface ProcessModelCopyContainerVisibilityFinding {
  nodeType: ProcessModelCopyNodeType.Area | ProcessModelCopyNodeType.Phase;
  modelId: string;
  /** The copied tasks' roles (after the role mapping) that the container is not visible for. */
  roleKeys: string[];
}
